import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Link,
  InputAdornment,
  IconButton,
  Divider,
  Alert,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Fade,
  Zoom,
  Stack,
  useTheme,
  alpha,
  LinearProgress,
  Collapse
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Google,
  Email,
  Lock,
  Person,
  PersonAdd,
  TrendingUp,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import { keyframes } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/authService';

// Animations - matching LoginForm
const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-3px);
  }
`;

const gradientShift = keyframes`
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
`;

const pulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(191, 155, 48, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(191, 155, 48, 0);
  }
`;

// Password strength checker
const checkPasswordStrength = (password) => {
  const criteria = [
    { label: 'At least 8 characters', test: (p) => p.length >= 8 },
    { label: 'Uppercase letter', test: (p) => /[A-Z]/.test(p) },
    { label: 'Lowercase letter', test: (p) => /[a-z]/.test(p) },
    { label: 'Number', test: (p) => /\d/.test(p) },
  ];
  
  const passed = criteria.filter(c => c.test(password)).length;
  const strength = (passed / criteria.length) * 100;
  
  return {
    strength,
    criteria: criteria.map(c => ({ ...c, passed: c.test(password) })),
    level: passed < 2 ? 'weak' : passed < 4 ? 'medium' : 'strong'
  };
};

const RegisterForm = ({ onClose, onSwitchToLogin }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState('');

  const navigate = useNavigate();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  const passwordStrength = checkPasswordStrength(formData.password);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim() || !formData.password.trim()) {
      setError('Please fill in all required fields');
      return;
    }
    
    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (passwordStrength.strength < 100) {
      setError('Password must meet all requirements');
      return;
    }
    
    if (!termsAccepted) {
      setError('You must accept the Terms and Conditions');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await authService.register({
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        email: formData.email.trim(),
        password: formData.password
      });
      
      if (onClose) onClose();
      window.location.reload();
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error.response?.data?.error || 'Registration failed. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignup = () => {
    console.log('Google signup clicked');
  };

  const handleSwitchToLogin = () => {
    if (onSwitchToLogin) {
      onSwitchToLogin();
    } else if (onClose) {
      onClose();
      // Navigate to home with login modal
      navigate('/?auth=login');
    } else {
      navigate('/?auth=login');
    }
  };

  const getPasswordStrengthColor = () => {
    switch (passwordStrength.level) {
      case 'weak': return theme.palette.error.main;
      case 'medium': return theme.palette.warning.main;
      case 'strong': return '#5980a6';
      default: return theme.palette.grey[400];
    }
  };

  // Common text field styles - matching LoginForm
  const textFieldSx = (fieldName) => ({
    '& .MuiOutlinedInput-root': {
      borderRadius: '2px',
      backgroundColor: alpha(theme.palette.background.paper, 0.8),
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: alpha(theme.palette.background.paper, 0.9),
      },
      '&.Mui-focused': {
        backgroundColor: alpha(theme.palette.background.paper, 1),
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.15)}`,
      }
    }
  });

  return (
    <Fade in timeout={600}>
      <Card 
        sx={{ 
          width: '100%',
          maxWidth: { xs: '100%', sm: 480 },
          mx: 'auto', 
          borderRadius: { xs: '2px', sm: '2px' },
          overflow: 'hidden',
          position: 'relative',
          background: isDarkMode
            ? 'linear-gradient(145deg, rgba(18,18,18,0.95) 0%, rgba(30,30,30,0.95) 100%)'
            : 'linear-gradient(145deg, rgba(255,255,255,0.95) 0%, rgba(250,250,250,0.95) 100%)',
          backdropFilter: 'none',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          boxShadow: `
            0 20px 40px ${alpha(theme.palette.common.black, 0.1)},
            0 0 0 1px ${alpha(theme.palette.primary.main, 0.05)},
            inset 0 1px 0 ${alpha(theme.palette.common.white, 0.1)}
          `,
          animation: { xs: 'none', sm: `${float} 6s ease-in-out infinite` },
        }}
      >
        {/* Gradient Background */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '100%',
            background: 'linear-gradient(135deg, #5980a6 0%, #7ba0c4 50%, #5980a6 100%)',
            backgroundSize: '200% 200%',
            animation: `${gradientShift} 8s ease infinite`,
            opacity: 0.05,
            zIndex: 0
          }}
        />
        
        <CardContent sx={{ 
          p: { xs: 3, sm: 5 }, 
          position: 'relative', 
          zIndex: 1 
        }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
            <Zoom in timeout={800}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: { xs: 64, sm: 80 },
                  height: { xs: 64, sm: 80 },
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #5980a6 0%, #7ba0c4 100%)',
                  mb: { xs: 2, sm: 3 },
                  animation: `${pulse} 2s infinite`,
                }}
              >
                <TrendingUp sx={{ fontSize: { xs: 32, sm: 40 }, color: 'white' }} />
              </Box>
            </Zoom>
            
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 800,
                fontSize: { xs: '1.5rem', sm: '2rem' },
                background: 'linear-gradient(135deg, #5980a6 0%, #7ba0c4 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}
            >
              Create Account
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                fontWeight: 500,
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Join XAUPilot and start trading smarter
            </Typography>
          </Box>

          {/* Error Alert */}
          <Collapse in={!!error}>
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3, 
                borderRadius: '2px',
                backgroundColor: alpha(theme.palette.error.main, 0.1),
                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
              }}
            >
              {error}
            </Alert>
          </Collapse>

          {/* Register Form */}
          <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
            <Stack spacing={{ xs: 2, sm: 2.5 }}>
              {/* Name Fields - Side by side on larger screens */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('firstName')}
                  onBlur={() => setFocusedField('')}
                  fullWidth
                  disabled={isSubmitting}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person 
                          sx={{ 
                            color: focusedField === 'firstName' ? 'primary.main' : 'text.secondary',
                            transition: 'color 0.2s ease'
                          }} 
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={textFieldSx('firstName')}
                />
                
                <TextField
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('lastName')}
                  onBlur={() => setFocusedField('')}
                  fullWidth
                  disabled={isSubmitting}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person 
                          sx={{ 
                            color: focusedField === 'lastName' ? 'primary.main' : 'text.secondary',
                            transition: 'color 0.2s ease'
                          }} 
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={textFieldSx('lastName')}
                />
              </Stack>

              {/* Email */}
              <TextField
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                onFocus={() => setFocusedField('email')}
                onBlur={() => setFocusedField('')}
                fullWidth
                disabled={isSubmitting}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email 
                        sx={{ 
                          color: focusedField === 'email' ? 'primary.main' : 'text.secondary',
                          transition: 'color 0.2s ease'
                        }} 
                      />
                    </InputAdornment>
                  ),
                }}
                sx={textFieldSx('email')}
              />

              {/* Password */}
              <TextField
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                onFocus={() => setFocusedField('password')}
                onBlur={() => setFocusedField('')}
                fullWidth
                disabled={isSubmitting}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock 
                        sx={{ 
                          color: focusedField === 'password' ? 'primary.main' : 'text.secondary',
                          transition: 'color 0.2s ease'
                        }} 
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={isSubmitting}
                        edge="end"
                        sx={{ 
                          color: 'text.secondary',
                          '&:hover': { 
                            color: 'primary.main',
                            backgroundColor: alpha(theme.palette.primary.main, 0.1)
                          }
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={textFieldSx('password')}
              />

              {/* Password Strength Indicator */}
              <Collapse in={formData.password.length > 0}>
                <Box sx={{ px: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={passwordStrength.strength}
                      sx={{
                        flex: 1,
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: alpha(getPasswordStrengthColor(), 0.2),
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 3,
                          backgroundColor: getPasswordStrengthColor(),
                        }
                      }}
                    />
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: getPasswordStrengthColor(),
                        fontWeight: 600,
                        textTransform: 'capitalize',
                        minWidth: 50
                      }}
                    >
                      {passwordStrength.level}
                    </Typography>
                  </Box>
                  
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {passwordStrength.criteria.map((c, i) => (
                      <Box 
                        key={i}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontSize: '0.7rem',
                          color: c.passed ? 'success.main' : 'text.disabled',
                        }}
                      >
                        {c.passed ? 
                          <CheckCircle sx={{ fontSize: 12 }} /> : 
                          <Cancel sx={{ fontSize: 12 }} />
                        }
                        <Typography variant="caption" sx={{ fontSize: 'inherit' }}>
                          {c.label}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Collapse>

              {/* Confirm Password */}
              <TextField
                label="Confirm Password"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                onFocus={() => setFocusedField('confirmPassword')}
                onBlur={() => setFocusedField('')}
                fullWidth
                disabled={isSubmitting}
                error={formData.confirmPassword.length > 0 && formData.password !== formData.confirmPassword}
                helperText={
                  formData.confirmPassword.length > 0 && formData.password !== formData.confirmPassword
                    ? 'Passwords do not match'
                    : ''
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock 
                        sx={{ 
                          color: focusedField === 'confirmPassword' ? 'primary.main' : 'text.secondary',
                          transition: 'color 0.2s ease'
                        }} 
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        disabled={isSubmitting}
                        edge="end"
                        sx={{ 
                          color: 'text.secondary',
                          '&:hover': { 
                            color: 'primary.main',
                            backgroundColor: alpha(theme.palette.primary.main, 0.1)
                          }
                        }}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={textFieldSx('confirmPassword')}
              />

              {/* Terms Checkbox */}
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    disabled={isSubmitting}
                    sx={{
                      color: 'primary.main',
                      '&.Mui-checked': {
                        color: 'primary.main',
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    I agree to the{' '}
                    <Link 
                      href="/terms" 
                      target="_blank"
                      sx={{ 
                        color: 'primary.main',
                        fontWeight: 600,
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      Terms of Service
                    </Link>
                    {' '}and{' '}
                    <Link 
                      href="/privacy"
                      target="_blank"
                      sx={{ 
                        color: 'primary.main',
                        fontWeight: 600,
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      Privacy Policy
                    </Link>
                  </Typography>
                }
                sx={{ 
                  alignItems: 'flex-start',
                  '& .MuiFormControlLabel-label': { pt: 0.5 }
                }}
              />

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <PersonAdd />}
                sx={{
                  borderRadius: '2px',
                  py: { xs: 1.5, sm: 1.8 },
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #5980a6 0%, #7ba0c4 100%)',
                  boxShadow: `0 8px 25px ${alpha('#5980a6', 0.4)}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7ba0c4 0%, #5980a6 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 35px ${alpha('#5980a6', 0.5)}`,
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                  }
                }}
              >
                {isSubmitting ? 'Creating Account...' : 'Create Account'}
              </Button>
            </Stack>
          </Box>

          {/* Divider */}
          <Divider sx={{ my: 3, opacity: 0.7 }}>
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, fontWeight: 500 }}>
              or continue with
            </Typography>
          </Divider>

          {/* Google Signup */}
          <Button
            variant="outlined"
            fullWidth
            size="large"
            disabled={isSubmitting}
            startIcon={<Google />}
            onClick={handleGoogleSignup}
            sx={{
              borderRadius: '2px',
              py: { xs: 1.2, sm: 1.5 },
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontWeight: 600,
              borderColor: alpha(theme.palette.divider, 0.8),
              color: 'text.primary',
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 15px ${alpha(theme.palette.primary.main, 0.1)}`,
              }
            }}
          >
            Continue with Google
          </Button>

          {/* Login Link */}
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <Link
                component="button"
                type="button"
                onClick={handleSwitchToLogin}
                disabled={isSubmitting}
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  fontWeight: 700,
                  '&:hover': {
                    textDecoration: 'underline',
                    color: 'primary.dark'
                  }
                }}
              >
                Sign in
              </Link>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Fade>
  );
};

export default RegisterForm;